import React, { FormEventHandler, useState, useEffect } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import PersonalInformationStep from "./PersonalInformationStep";
import SystemInformationStep from "./SystemInformationStep";
import IncentiveCalculationStep from "./IncentiveCalculationStep";
import PayeeInformationStep from "./PayeeInformationStep";
import ParticipantEnrollmentStep from "./ParticipantEnrollmentStep";
import useAccountValidation from "@/hooks/useAccountValidation";
import useIncentiveImpactedFields from "@/hooks/useIncentiveImpactedFields";
import IncentiveImpactWarning from "@/components/IncentiveImpactWarning";
import { useAuthStore } from "@/store/authStore.ts";

import { PROJECT_STEPS, ProjectFormMode, ProjectStep } from "@/constants";

const MultiStepProjectForm = ({
  handleSubmit,
  handleClose,
  handleStep,
  step,
  mode = "create",
  isSubmitting = false,
  projectId,
}: {
  handleSubmit: FormEventHandler<HTMLFormElement>;
  handleClose: () => void;
  handleStep: (step: ProjectStep) => void;
  step: ProjectStep;
  mode?: ProjectFormMode;
  isSubmitting?: boolean;
  /** Only present when editing — a project must exist before it can be recalculated. */
  projectId?: number;
}) => {
  const { control, formState } = useFormContext();

  // SDCP-1310: same signal as the inline warning, so the button label can't disagree with it.
  const role = useAuthStore((state) => state.role);
  const { hasIncentiveImpact } = useIncentiveImpactedFields(
    mode === "edit" && role === "administrator"
  );

  const watchedFields = useWatch({ control });

  const shouldRenderForm = step === PROJECT_STEPS.PARTICIPANT_ENROLLMENT;

  const {
    customerName,
    accountHash,
    zipCode,
    streetAddress,
    city,
    state,
    accountEmail,

    solarSystemType,
    solarKwdc,
    solarKwac,
    solarAnnualProduction,
    newSolarKwdc,
    newSolarKwac,
    newSolarAnnualProduction,
    contractor,
    contractorId,
    batteryManufacturer,
    batteryOemId,
    batteryProductName,
    usableCapacity,
    batteryQuantity,
    totalProjectCost,
    termsAndConditionsFile,
    requiresTermsAndConditions,

    upfrontIncentivePayment,
    payeeType,
    payeeName,
    payeeEmail,
    payeeAddress,
    payeeCity,
    payeeState,
    payeeZip,
    estimated_installation_date,
  } = watchedFields;

  const { validationResult, isValidating, validateAccount } =
    useAccountValidation(500);
  const [accountValidationState, setAccountValidationState] = useState<{
    isValid: boolean;
    isChecking: boolean;
  }>({ isValid: false, isChecking: false });

  useEffect(() => {
    if (accountHash && zipCode) {
      validateAccount(accountHash, zipCode);
    }
  }, [accountHash, zipCode, validateAccount]);

  useEffect(() => {
    if (accountHash) {
      setAccountValidationState({
        isValid: validationResult?.isValid || false,
        isChecking: isValidating,
      });
    } else {
      setAccountValidationState({ isValid: false, isChecking: false });
    }
  }, [validationResult, isValidating, accountHash]);

  const isStep1Valid = () => {
    const valid = Boolean(
      customerName?.trim() &&
        zipCode?.trim() &&
        accountHash?.trim() &&
        validationResult?.isValid &&
        validationResult?.zipCodeMatch !== false &&
        !isValidating
    );

    return valid;
  };

  const isStep2Valid = () => {
    const solarSystemTypesValid =
      solarSystemType &&
      Array.isArray(solarSystemType) &&
      solarSystemType.length > 0;

    if (!solarSystemTypesValid) {
      return false;
    }

    const hasExistingSolar = solarSystemType.includes("existing");
    const hasNewSolar = solarSystemType.includes("new");

    const isSolarFieldsValid = (fields: {
      kwdc: string | number | undefined;
      kwac: string | number | undefined;
      annualProduction: string | number | undefined;
    }): boolean => {
      return Object.values(fields).every((value) => {
        if (value === undefined || value === "" || value === 0) {
          return true;
        }
        const numericValue = Number(value);
        return !isNaN(numericValue) && numericValue > 0;
      });
    };

    let solarFieldsValid = true;
    if (hasExistingSolar) {
      const existingSolarValid = {
        solarKwdc:
          solarKwdc !== undefined &&
          solarKwdc !== null &&
          String(solarKwdc).trim() !== "",
        solarKwac:
          solarKwac !== undefined &&
          solarKwac !== null &&
          String(solarKwac).trim() !== "",
        solarAnnualProduction:
          solarAnnualProduction !== undefined &&
          solarAnnualProduction !== null &&
          String(solarAnnualProduction).trim() !== "",
      };

      solarFieldsValid =
        solarFieldsValid &&
        existingSolarValid.solarKwdc &&
        existingSolarValid.solarKwac &&
        existingSolarValid.solarAnnualProduction;
    }

    if (hasNewSolar) {
      const newSolarValid = {
        newSolarKwdc:
          newSolarKwdc !== undefined &&
          newSolarKwdc !== null &&
          String(newSolarKwdc).trim() !== "",
        newSolarKwac:
          newSolarKwac !== undefined &&
          newSolarKwac !== null &&
          String(newSolarKwac).trim() !== "",
        newSolarAnnualProduction:
          newSolarAnnualProduction !== undefined &&
          newSolarAnnualProduction !== null &&
          String(newSolarAnnualProduction).trim() !== "",
      };

      solarFieldsValid =
        solarFieldsValid &&
        newSolarValid.newSolarKwdc &&
        newSolarValid.newSolarKwac &&
        newSolarValid.newSolarAnnualProduction;
    }

    const fieldChecks = {
      contractor: {
        value: contractor,
        trimmed: contractor?.trim(),
        valid: Boolean(contractor?.trim()),
      },
      contractorId: {
        value: contractorId,
        toString: contractorId?.toString(),
        trimmed: contractorId?.toString().trim(),
        valid: Boolean(contractorId?.toString().trim()),
      },
      batteryManufacturer: {
        value: batteryManufacturer,
        trimmed: batteryManufacturer?.trim(),
        valid: Boolean(batteryManufacturer?.trim()),
      },

      batteryProductName: {
        value: batteryProductName,
        trimmed: batteryProductName?.trim(),
        valid: Boolean(batteryProductName?.trim()),
      },
      usableCapacity: {
        value: usableCapacity,
        toString: usableCapacity?.toString(),
        trimmed: usableCapacity?.toString().trim(),
        valid: Boolean(usableCapacity?.toString().trim()),
      },
      batteryQuantity: {
        value: batteryQuantity,
        toString: batteryQuantity?.toString(),
        trimmed: batteryQuantity?.toString().trim(),
        valid: Boolean(batteryQuantity?.toString().trim()),
      },
      totalProjectCost: {
        value: totalProjectCost,
        toString: totalProjectCost?.toString(),
        trimmed: totalProjectCost?.toString().trim(),
        valid: Boolean(totalProjectCost?.toString().trim()),
      },
      streetAddress: {
        value: streetAddress,
        trimmed: streetAddress?.trim(),
        valid: Boolean(streetAddress?.trim()),
      },
      city: {
        value: city,
        trimmed: city?.trim(),
        valid: Boolean(city?.trim()),
      },
      state: {
        value: state,
        trimmed: state?.trim(),
        valid: Boolean(state?.trim()),
      },
      solarFieldsValid: {
        value: solarFieldsValid,
        valid: solarFieldsValid,
      },
    };

    const basicFieldsValid = Boolean(
      contractor?.trim() &&
        contractorId?.toString().trim() &&
        batteryManufacturer?.trim() &&
        batteryProductName?.trim() &&
        usableCapacity?.toString().trim() &&
        batteryQuantity?.toString().trim() &&
        totalProjectCost?.toString().trim() &&
        streetAddress?.trim() &&
        city?.trim() &&
        state?.trim() &&
        accountEmail?.trim() &&
        !formState.errors.accountEmail &&
        solarFieldsValid
    );

    const termsAndConditionsValid =
      !requiresTermsAndConditions || !!termsAndConditionsFile;

    return basicFieldsValid && termsAndConditionsValid;
  };

  const isStep3Valid = () => {
    // SDCP-1305: when editing, this step only displays the stored incentive — it is no longer
    // an input, so it must not block progress. A project legitimately sitting at $0 has to
    // stay editable.
    if (mode === "edit") return true;

    const valid = Boolean(
      upfrontIncentivePayment && Number(upfrontIncentivePayment) > 0
    );

    return valid;
  };

  const isStep4Valid = () => {
    const valid = Boolean(
      payeeType?.trim() &&
        payeeName?.trim() &&
        payeeAddress?.trim() &&
        payeeCity?.trim() &&
        payeeState?.trim() &&
        payeeZip?.trim()
    );

    return valid;
  };

  const isStep5Valid = () => {
    return true;
  };

  const isButtonDisabled = () => {
    const stepValidations = {
      [PROJECT_STEPS.PERSONAL_INFO]: !isStep1Valid(),
      [PROJECT_STEPS.SYSTEM_INFO]: !isStep2Valid(),
      [PROJECT_STEPS.INCENTIVE_CALC]: !isStep3Valid(),
      [PROJECT_STEPS.PAYEE_INFO]: !isStep4Valid(),
      [PROJECT_STEPS.PARTICIPANT_ENROLLMENT]: !isStep5Valid(),
    };

    const isDisabled =
      stepValidations[step as keyof typeof stepValidations] || false;

    return isDisabled;
  };

  const content = (
    <>
      <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
        {step === PROJECT_STEPS.PERSONAL_INFO && <PersonalInformationStep />}
        {step === PROJECT_STEPS.SYSTEM_INFO && (
          <SystemInformationStep mode={mode} projectId={projectId} />
        )}
        {step === PROJECT_STEPS.INCENTIVE_CALC && (
          <IncentiveCalculationStep mode={mode} />
        )}
        {step === PROJECT_STEPS.PAYEE_INFO && <PayeeInformationStep />}
        {step === PROJECT_STEPS.PARTICIPANT_ENROLLMENT && (
          <ParticipantEnrollmentStep />
        )}
      </Box>

      {/*
        SDCP-1310: the recalc warning renders here, at the bottom of the modal just above the
        action buttons (client request), instead of inline in the middle of the step. Same
        signal as the "Recalculate" button label, and only on the System Information step, so
        the two can never disagree.
      */}
      {step === PROJECT_STEPS.SYSTEM_INFO && (
        <Box sx={{ flexShrink: 0 }}>
          <IncentiveImpactWarning
            show={hasIncentiveImpact}
            sx={{ mt: 3, mb: 0 }}
          />
        </Box>
      )}

      <Box
        sx={{
          marginTop: "32px",
          display: "flex",
          gap: "8px",
          width: "100%",
          flexShrink: 0,
        }}
      >
        <Button
          variant={"outlined"}
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderColor: "#1976d2",
            color: "#1976d2",
            "&:hover": {
              backgroundColor: "white",
              borderColor: "#1565c0",
              color: "#1565c0",
            },
          }}
          onClick={() => {
            step === PROJECT_STEPS.PERSONAL_INFO
              ? handleClose()
              : handleStep((step - 1) as ProjectStep);
          }}
          disabled={isSubmitting}
        >
          {step === 1 ? "Cancel" : "Previous"}
        </Button>

        <Button
          variant={"contained"}
          type={
            step === PROJECT_STEPS.PARTICIPANT_ENROLLMENT ? "submit" : "button"
          }
          sx={{
            width: "100%",
            backgroundColor: "#1976d2",
            color: "white",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            "&:disabled": {
              backgroundColor: "#E3F2FD",
              color: "#999",
            },
          }}
          onClick={
            step === PROJECT_STEPS.PARTICIPANT_ENROLLMENT
              ? undefined
              : () => {
                  if (step < PROJECT_STEPS.PARTICIPANT_ENROLLMENT) {
                    handleStep((step + 1) as ProjectStep);
                  }
                }
          }
          disabled={isButtonDisabled() || isSubmitting}
        >
          {isSubmitting &&
          step === PROJECT_STEPS.PARTICIPANT_ENROLLMENT ? (
            <>
              <CircularProgress size={20} sx={{ color: "#999", mr: 1 }} />
              {mode === "edit" ? "Updating..." : "Creating..."}
            </>
          ) : step === PROJECT_STEPS.PERSONAL_INFO ? (
            mode === "edit" ? (
              "Update"
            ) : (
              "Create"
            )
          ) : step === PROJECT_STEPS.PARTICIPANT_ENROLLMENT ? (
            mode === "edit" ? (
              "Update Project"
            ) : (
              "Create Project"
            )
          ) : step === PROJECT_STEPS.SYSTEM_INFO && hasIncentiveImpact ? (
            "Recalculate"
          ) : (
            "Next"
          )}
        </Button>
      </Box>
    </>
  );

  // Only render form on step 5, otherwise just show content
  if (shouldRenderForm) {
    return (
      <form
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          marginTop: "24px",
        }}
        onSubmit={handleSubmit}
        noValidate
      >
        {content}
      </form>
    );
  }

  // For steps 1-4, just render the content without a form
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        marginTop: "24px",
      }}
    >
      {content}
    </Box>
  );
};

export default MultiStepProjectForm;
